import React from 'react'
import ReactDOM from 'react-dom/client'
import './index.css'
import './styles/border.css'
import './styles/colors.css'
import './styles/fonts.css'
import './styles/gaps.css'
import {
  createAgent,
  MeshOutNotification,
  MessagingService,
  MessagingServiceProvider,
  ReauthNeededInfoSaver,
} from './agent'
import MeshSite from './ui/meshsite'
import { MeshAppBridge } from './meshAppBridge/bridge'
import {
  agentRestUrl,
  isLocalhost,
  localRequiresAuth,
  localEnableSpaceNotifications,
} from './environment'
import { LookupSiteResponsePayload } from './agent/messages'
import { Id } from './aliases'
import { siteInfoStore } from './siteInfo/store'
import { SiteInfoProvider } from './siteInfo/provider'
import { routes } from './routes'
import AgentStatusCodes from './agent/agentStatusCodes'

const agent = createAgent()
const messagingService = new MessagingService(agent)
const meshAppBridge = new MeshAppBridge(agent)
const reauthInfoSaver = new ReauthNeededInfoSaver()

// Site auth requirements — set after fetchSiteConfig, used by reauth handler
let siteRequiresAuth = false
let siteEnableSpaceNotifications = false

window.addEventListener('load', async () => {
  reauthInfoSaver.clearReauthNeededInfo()

  try {
    await agent.initialize()
  } catch (err) {
    console.error('Error initializing agent:', err)
  }

  if (!agent.isAuthenticated()) {
    console.error('Agent failed to authenticate (including anonymous fallback)')
    if (window.location.pathname !== '/') {
      meshOut(true)
    }
    return
  }

  // Determine site auth requirements
  const siteName = window.location.pathname

  if (isLocalhost) {
    // In local mode, use env var overrides since lookupSite may not work for local paths
    siteRequiresAuth = localRequiresAuth
    siteEnableSpaceNotifications = localEnableSpaceNotifications
    console.log(
      `Local mode — requiresAuth=${siteRequiresAuth}, spaceNotifications=${siteEnableSpaceNotifications}`,
    )
  } else {
    const config = await fetchSiteConfig(siteName)
    if (!config) {
      console.error('Failed to fetch site config:', siteName)
      if (window.location.pathname !== '/') {
        meshOut(true)
      }
      return
    }

    siteRequiresAuth = config.requires_auth
    siteEnableSpaceNotifications = config.needs_space_notifications
    console.log(
      `Site ${siteName} — requiresAuth=${siteRequiresAuth}, spaceNotifications=${siteEnableSpaceNotifications}`,
    )
  }

  // If site requires auth and we only have anonymous, need real auth
  if (siteRequiresAuth && agent.isAnonymous()) {
    console.log(
      'Site requires auth but session is anonymous — redirecting to reauth',
    )
    reauth()
    return
  }

  // Now install the reauth listener for mid-session failures
  agent.setReauthenticationNeededListener(onReauthenticationNeeded)
  agent.addMeshOutNotificationListener(onMeshOut)

  meshAppBridge.enableReconnectedNotifications()

  if (siteEnableSpaceNotifications) {
    meshAppBridge.enableSpaceNotifications()
  }

  if (!agent.isAnonymous() && isLocalhost) {
    await fetchInitialData()
  }

  const root = ReactDOM.createRoot(
    document.getElementById('root') as HTMLElement,
  )
  root.render(
    <React.StrictMode>
      <SiteInfoProvider>
        <MessagingServiceProvider service={messagingService}>
          <MeshSite bridge={meshAppBridge} />
        </MessagingServiceProvider>
      </SiteInfoProvider>
    </React.StrictMode>,
  )
})

type SiteConfig = {
  requires_auth: boolean
  requires_meshfa: boolean
  needs_space_notifications: boolean
}

async function fetchSiteConfig(siteName: string): Promise<SiteConfig | null> {
  try {
    const response = await fetch(
      `${agentRestUrl}/entities/meshin/site/config${siteName}`,
    )
    if (!response.ok) {
      console.warn('fetchSiteConfig failed with status:', response.status)
      return null
    }
    return await response.json()
  } catch (err) {
    console.error('fetchSiteConfig error:', err)
    return null
  }
}

window.addEventListener('message', async event => {
  if (event.data?.type === 'lookupSiteRequest') {
    console.log('Received lookup site request:', event.data)
    const port = event.ports[0]

    try {
      const siteName = event.data.siteName
      console.log('[Parent] Looking up site:', siteName)
      const response = await messagingService.lookupSite(siteName)
      const siteInfo = response?.payload
      console.log(
        '[Parent] Lookup site response:',
        response?.payload?.proxyLinkCode,
      )

      console.log(
        '[Parent] Lookup site response.header.status: ',
        response.header.status,
      ) // Log auth requirement
      if (response.header.status === AgentStatusCodes.RequestDenied) {
        window.location.href = routes.noAuthority
        return
      }
      siteInfoStore.set(siteInfo ?? null)
      console.log('[Parent] Updated siteInfoStore with:', siteInfo)
      meshAppBridge.setInitialData(siteInfo)

      port.postMessage(siteInfo)
      if (siteInfo) {
        checkIfReauthNeeded(siteInfo)
      }
    } catch (err) {
      console.warn('[Parent] Could not get siteInfo:', err)
      port.postMessage({})
    }
  }
})

function checkIfReauthNeeded(lookupSiteResponse: LookupSiteResponsePayload) {
  console.log('Checking if reauth needed with site info:', lookupSiteResponse)
  const {
    needToAcceptOrganizationTos,
    proxyLinkCode,
    org,
    needsReauth,
    hasMeshfaClient,
  } = lookupSiteResponse
  if (!needsReauth && !needToAcceptOrganizationTos) return
  reauth(hasMeshfaClient, proxyLinkCode, org?.publicId)
}

function meshOut(canReturn: boolean) {
  console.log('Mesh out. Can return:', canReturn)
  if (canReturn) {
    reauthInfoSaver.saveReauthNeededInfo()
  }
  window.location.href = '/'
}

function onMeshOut(_: MeshOutNotification) {
  meshOut(false)
}

function reauth(
  hasMeshfaClient?: boolean,
  humanProxyId?: Id,
  orgPublicId?: Id,
) {
  const url = orgPublicId ? routes.orgReauth(orgPublicId) : routes.reauth

  console.log(
    'Reauth needed. hasMeshfaClient:',
    hasMeshfaClient,
    'humanProxyId:',
    humanProxyId,
    'orgPublicId:',
    orgPublicId,
    'path:',
    url,
  )
  reauthInfoSaver.saveReauthNeededInfo(hasMeshfaClient, humanProxyId)
  window.location.href = url
}

async function fetchInitialData() {
  ////////// Temporary code to get proxy link code and pass it to the bridge before checking MeshFA status
  try {
    const response = await messagingService.lookupSite(
      // '/orgs/nYfD-w-6J6TqDFWcdzurT0Fi8IPxOxmtXHseIvajnrk/admin',
      '/create',
    )
    console.log(
      'Lookup site response in fetchInitialData:',
      response.header.status,
    )
    if (response.header.status === AgentStatusCodes.RequestDenied) {
      window.location.href = routes.noAuthority
      return
    }
    const siteInfo = response?.payload
    meshAppBridge.setInitialData(siteInfo)

    siteInfoStore.set(siteInfo ?? null)
    if (siteInfo) {
      checkIfReauthNeeded(siteInfo)
    }
  } catch (err) {
    console.warn('fetchInitialData failed:', err)
  }
  //////////
}

function onReauthenticationNeeded() {
  if (!siteRequiresAuth) {
    // Site allows anonymous — state was already reset by sendMessageReceiveReply.
    // Reload: next page load will create a fresh anonymous session.
    console.log(
      'Session expired but site allows anonymous — reloading with anonymous fallback',
    )
    window.location.reload()
    return
  }
  const siteInfo = meshAppBridge.getInitialData()
  if (!siteInfo) return
  checkIfReauthNeeded({ ...siteInfo, needsReauth: true })
}
